import { useState, useEffect, useCallback } from 'react';
import { AxiosAmbient } from 'src/lib';
import { FronteggContext } from '@frontegg/rest-api';

interface ZoomStatus {
  isConnected: boolean;
  needsReauth: boolean;
  lastChecked: Date | null;
}

export const useZoomStatusCheck = (
  checkInterval: number = 900000,
  enabled: boolean = true
) => {
  const [status, setStatus] = useState<ZoomStatus>({
    isConnected: false,
    needsReauth: false,
    lastChecked: null,
  });

  const checkZoomStatus = useCallback(async () => {
    if (!enabled) return;

    try {
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
      };

      const result = await AxiosAmbient.get('/zoom-connection-status', {
        headers,
      });

      const response = result?.data;
      setStatus({
        isConnected: response?.connected === true,
        needsReauth: response?.needs_reauth === true,
        lastChecked: new Date(),
      });
    } catch (error) {
      console.error('Failed to check zoom status:', error);
      setStatus((prev) => ({
        ...prev,
        isConnected: false,
        needsReauth: true,
        lastChecked: new Date(),
      }));
    }
  }, [enabled]);

  // Initial check
  useEffect(() => {
    if (enabled) {
      checkZoomStatus();
    }
  }, [enabled, checkZoomStatus]);

  // Periodic checking
  useEffect(() => {
    if (!enabled) return;

    const intervalId = setInterval(() => {
      checkZoomStatus();
    }, checkInterval);

    return () => clearInterval(intervalId);
  }, [enabled, checkInterval, checkZoomStatus]);

  return {
    ...status,
    checkZoomStatus,
  };
};
